import { useQuery } from "@tanstack/react-query";
import {
  getManagerImpact,
  getManagerPortfolio,
  getManagerVentures,
  getManagerVenture,
  getManagerVentureDueDiligence,
  getManagerDataCollection,
  getManagerReports,
  getManagerTraining,
} from "../api";
import { useAuth } from "../context/authContext";

export function useManagerPortfolioHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["managerPortfolio", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerPortfolio({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to load portfolio data.");
      }

      return result.portfolio;
    },
  });
}

export function useManagerImpactHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["managerImpact", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerImpact({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to load impact data.");
      }

      return result.impact;
    },
  });
}

export function useManagerVenturesHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["managerVentures", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerVentures({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to load ventures data.");
      }

      return result.ventures;
    },
  });
}

export function useManagerVentureHook({ token, ventureId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;

  return useQuery({
    queryKey: ["managerVenture", ventureId],
    enabled: enabled && !!resolvedToken && !!ventureId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerVenture({
        token: resolvedToken,
        ventureId,
      });

      if (!result.ok) {
        throw new Error(result.error || "Unable to load venture details.");
      }

      return result.venture;
    },
  });
}

export function useManagerVentureDueDiligenceHook({
  token,
  ventureId,
  enabled = true,
}) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;

  return useQuery({
    queryKey: ["managerVentureDueDiligence", ventureId],
    enabled: enabled && !!resolvedToken && !!ventureId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerVentureDueDiligence({
        token: resolvedToken,
        ventureId,
      });
      if (!result.ok) {
        throw new Error(
          result.error || "Unable to load venture due diligence score.",
        );
      }
      return result.dueDiligence;
    },
  });
}

export function useManagerDataCollectionHook({
  token,
  userId,
  enabled = true,
}) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["managerDataCollection", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerDataCollection({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch data collection data");
      }

      return result.dataCollection;
    },
  });
}

export function useManagerReportsHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["managerReports", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerReports({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch reports data");
      }

      return result.reports;
    },
  });
}

export function useManagerTrainingHook({ token, userId, enabled = true }) {
  const auth = useAuth();
  const resolvedToken = token ?? auth.token;
  const resolvedUserId = userId ?? auth.user?.id;

  return useQuery({
    queryKey: ["managerTraining", resolvedUserId],
    enabled: enabled && !!resolvedToken && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const result = await getManagerTraining({ token: resolvedToken });

      if (!result.ok) {
        throw new Error(result.error || "Unable to fetch training data");
      }

      return result.training;
    },
  });
}
